// The "Updated" date shown on a blog post, and the matching `dateModified` for its schema.
//
// Editors set `custom_updated_date` (a plain `YYYY-MM-DD`) in WordPress when they refresh a
// post's content, so when it is present it is the update date. Posts without it fall back to
// WordPress's own `modified`, which bumps on every save — that one only counts as an update
// when it lands in a different month or year than the publish date (a same-month tweak is
// not a refresh worth announcing).

interface BlogDates {
    date?: string;
    modified?: string;
    custom_updated_date?: string;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const toTime = (value?: string): number => (value ? new Date(value).getTime() : Number.NaN);

const isValidCustomDate = (custom: string | undefined, published?: string): custom is string => {
    if (!custom || !DATE_ONLY.test(custom.trim())) return false;
    const updated = toTime(custom.trim());
    if (Number.isNaN(updated)) return false;
    // An update dated before the post existed is a data-entry slip; ignore it. The publish
    // timestamp is trimmed to its day so an update on the publish day itself still counts.
    const publishedDay = toTime(published?.slice(0, 10));
    return Number.isNaN(publishedDay) || updated >= publishedDay;
};

const isMeaningfulModified = (published?: string, modified?: string): boolean => {
    const p = new Date(published ?? '');
    const m = new Date(modified ?? '');
    if (Number.isNaN(p.getTime()) || Number.isNaN(m.getTime())) return false;
    return m.getFullYear() !== p.getFullYear() || m.getMonth() !== p.getMonth();
};

/** The date to show as "Updated", or `null` when the post has no update worth showing. */
export const getBlogUpdatedDate = (post?: BlogDates | null): string | null => {
    if (!post) return null;
    if (isValidCustomDate(post.custom_updated_date, post.date)) return post.custom_updated_date.trim();
    return isMeaningfulModified(post.date, post.modified) ? post.modified ?? null : null;
};

/** `dateModified` for the post's schema — the editor's update date first, so it matches the page. */
export const getBlogModifiedDate = (post?: BlogDates | null): string | undefined => {
    if (!post) return undefined;
    if (isValidCustomDate(post.custom_updated_date, post.date)) return post.custom_updated_date.trim();
    return post.modified || post.date;
};

/**
 * "September 18, 2026". A date-only `YYYY-MM-DD` is formatted in UTC: `new Date` reads it as
 * UTC midnight, so formatting it in a local zone west of UTC would show the previous day and
 * the server-rendered and hydrated text could disagree.
 */
export const formatBlogDate = (value: string): string =>
    new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...(DATE_ONLY.test(value) && {timeZone: 'UTC'})
    });

/**
 * The single date a listing card shows: the editor's update date (labelled "Updated") when
 * set, otherwise the publish date. A date-only update is returned as local midnight, so the
 * card's local-time formatters print that same day in every time zone.
 */
export const getListingDate = (post?: BlogDates | null): {updated: boolean; value: string} => {
    if (post && isValidCustomDate(post.custom_updated_date, post.date)) {
        return {updated: true, value: `${post.custom_updated_date.trim()}T00:00:00`};
    }
    return {updated: false, value: post?.date ?? ''};
};

/** A card's date line, "Updated September 18, 2026" or just the publish date, in the card's own format. */
export const formatListingDate = (post: BlogDates | null | undefined, format: (value: string) => string): string => {
    const {updated, value} = getListingDate(post);
    if (!value) return '';
    return updated ? `Updated ${format(value)}` : format(value);
};
